import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { FieldError } from '@/components/field-error'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { fromLocalInput, toLocalInput, utcLabel } from '@/lib/datetime'
import { useDebounced } from '@/lib/use-debounced'
import {
  SORT_ORDER_INPUT,
  editPatch,
  sponsoredSchema,
  useCoinSearch,
  useSaveSponsoredCoin,
  type CoinSearchResult,
  type SponsoredCoin,
  type SponsoredCoinRef,
  type SponsoredInput,
} from '@/features/sponsored-coins/api'

function toFormValues(row?: SponsoredCoin, nextSortOrder = 1): SponsoredInput {
  return {
    // 0 is "nothing picked yet"; the schema rejects it with "Pick a coin."
    coin_id: row?.coin?.id ?? 0,
    sort_order: row?.sort_order ?? nextSortOrder,
    state: row?.state ?? 'off',
    starts_at: row?.starts_at ?? null,
    ends_at: row?.ends_at ?? null,
  }
}

interface SponsoredEditorProps {
  /** Absent for a new entry. */
  row?: SponsoredCoin
  /** A new entry's default order: one after the last row. */
  nextSortOrder: number
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Constructed by the page only once the list has loaded, keyed per row, so defaultValues are the row's. */
export function SponsoredEditor({ row, nextSortOrder, open, onOpenChange }: SponsoredEditorProps) {
  const save = useSaveSponsoredCoin()
  const [picked, setPicked] = useState<SponsoredCoinRef | null>(row?.coin ?? null)

  const form = useForm<SponsoredInput>({
    resolver: standardSchemaResolver(sponsoredSchema),
    defaultValues: toFormValues(row, nextSortOrder),
  })
  // Read during render: react-hook-form only tracks a formState slice once a
  // render has read it, and editPatch needs dirtyFields at submit.
  const { dirtyFields, isDirty } = form.formState
  const startsAt = form.watch('starts_at')
  const endsAt = form.watch('ends_at')

  function setWindow(field: 'starts_at' | 'ends_at', value: string) {
    form.setValue(field, fromLocalInput(value), { shouldDirty: true, shouldValidate: true })
  }

  function pick(coin: SponsoredCoinRef) {
    setPicked(coin)
    form.setValue('coin_id', coin.id, { shouldDirty: true, shouldValidate: true })
  }

  // Clears the coin in the form too, so saving without picking again fails with
  // "Pick a coin." instead of quietly keeping the old one.
  function changeCoin() {
    setPicked(null)
    form.setValue('coin_id', 0, { shouldDirty: true })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) save.reset()
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{row ? 'Edit sponsored coin' : 'New sponsored coin'}</DialogTitle>
          <DialogDescription>
            Pinned above the homepage Top list with a <b>Sponsored</b> tag. Shown only when{' '}
            <b>State is Live AND the current time falls within the window</b> — leave the window
            empty to go by State alone.
          </DialogDescription>
        </DialogHeader>

        <form
          id="sponsored-form"
          className="space-y-5"
          onSubmit={form.handleSubmit((values) =>
            save.mutate(
              { id: row?.id, input: row ? editPatch(values, dirtyFields) : values },
              { onSuccess: () => onOpenChange(false) },
            ),
          )}
        >
          <div className="space-y-2">
            <Label htmlFor="coin-search">Coin</Label>
            {row && !row.coin && !picked && (
              <p className="text-xs text-destructive">
                The coin this entry named has been removed from the catalog. Pick another, or
                switch the entry off.
              </p>
            )}
            {picked ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border p-2.5">
                <CoinLabel coin={picked} />
                <Button type="button" variant="secondary" size="sm" onClick={changeCoin}>
                  Change
                </Button>
              </div>
            ) : (
              <CoinPicker onPick={pick} />
            )}
            {picked && !picked.has_market && (
              <p className="text-xs text-warning">
                This coin has no market data, so the homepage cannot build its row and will not
                show it.
              </p>
            )}
            <FieldError message={form.formState.errors.coin_id?.message} />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="sort_order">Order</Label>
              <Input id="sort_order" type="number" min={0} {...form.register('sort_order', SORT_ORDER_INPUT)} />
              <p className="text-xs text-faint">Smaller first, when more than one is live.</p>
              <FieldError message={form.formState.errors.sort_order?.message} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sponsored-state">State</Label>
              <Select
                value={form.watch('state')}
                onValueChange={(value) =>
                  form.setValue('state', value as SponsoredInput['state'], { shouldDirty: true })
                }
              >
                <SelectTrigger id="sponsored-state">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="off">Off (hidden)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="sponsored-starts">Starts (optional, your local time)</Label>
              <Input
                id="sponsored-starts"
                type="datetime-local"
                value={toLocalInput(startsAt)}
                onChange={(event) => setWindow('starts_at', event.target.value)}
              />
              <p className="text-xs text-faint">
                {startsAt ? utcLabel(startsAt) : 'Empty — live the moment State is set to Live.'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sponsored-ends">Ends (optional, your local time)</Label>
              <Input
                id="sponsored-ends"
                type="datetime-local"
                value={toLocalInput(endsAt)}
                onChange={(event) => setWindow('ends_at', event.target.value)}
              />
              <p className="text-xs text-faint">{endsAt ? utcLabel(endsAt) : 'Empty — no expiry.'}</p>
              <FieldError message={form.formState.errors.ends_at?.message} />
            </div>
          </div>

          {save.isError && <p className="text-xs text-destructive">{(save.error as Error).message}</p>}
        </form>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {/* An edit with nothing changed has nothing to send, as the banner page does. */}
          <Button type="submit" form="sponsored-form" disabled={save.isPending || (row !== undefined && !isDirty)}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Search by name, symbol or slug. Results come exact-symbol first, then by
 * market cap, and show the slug and market cap: the catalog is full of tokens
 * that copy a well-known symbol, and sponsoring the wrong one is the mistake
 * this screen most needs to prevent.
 */
function CoinPicker({ onPick }: { onPick: (coin: CoinSearchResult) => void }) {
  const [text, setText] = useState('')
  const q = useDebounced(text)
  const search = useCoinSearch(q)

  return (
    <div className="space-y-2">
      <Input
        id="coin-search"
        placeholder="Search by name, symbol or slug — e.g. SOL"
        value={text}
        onChange={(event) => setText(event.target.value)}
        autoComplete="off"
      />
      {q.trim() !== '' && (
        <div className="max-h-64 overflow-y-auto rounded-lg border">
          {search.isPending ? (
            <p className="p-3 text-xs text-muted-foreground">Searching…</p>
          ) : search.isError ? (
            <p className="p-3 text-xs text-destructive">{(search.error as Error).message}</p>
          ) : search.data.length === 0 ? (
            <p className="p-3 text-xs text-muted-foreground">No crypto coin matches “{q.trim()}”.</p>
          ) : (
            search.data.map((coin) => (
              <button
                key={coin.id}
                type="button"
                className="flex w-full items-center justify-between gap-3 border-b px-3 py-2 text-left last:border-b-0 hover:bg-secondary"
                onClick={() => onPick(coin)}
              >
                <CoinLabel coin={coin} />
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {/* A coin with a market row but no market cap (e.g. no circulating supply) is still shown on the site. */}
                  {coin.market_cap ? formatCap(coin.market_cap) : coin.has_market ? 'Market cap —' : 'No market data'}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export function CoinLabel({ coin }: { coin: SponsoredCoinRef }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <div className="size-7 shrink-0 overflow-hidden rounded-full bg-secondary">
        {coin.logo && <img src={coin.logo} alt="" className="size-full object-cover" />}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold">{coin.symbol}</span>
          <span className="truncate text-sm">{coin.name}</span>
          {!coin.has_market && <Badge variant="warning">No market data</Badge>}
        </div>
        <div className="truncate text-xs text-faint">{coin.slug}</div>
      </div>
    </div>
  )
}

/** Market cap for telling coins apart, not for reading off: "$83.46B". Display only. */
function formatCap(value: string): string {
  const n = Number(value)
  if (!Number.isFinite(n)) return value
  return `$${new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 2 }).format(n)}`
}
